import { Button } from '@/global/components/Button'
import { FormField } from '@/global/components/Form'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import { useCalculo } from '@/hooks/useCalculo'
import { calculo } from '@/service/api'
import { useStore } from '@/store'
import { SN } from '@acme/core'
import { CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { z } from 'zod'
import { ViewMode } from '../toggle-view'

export default function ButtonCalculo({
    competencia,
    view,
}: {
    competencia: string
    view: ViewMode
}) {
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const [open, setOpen] = useState<SN | null>(null)
    const [isCalculando, setIsCalculando] = useState(false)
    const { handleCalculo } = useCalculo()

    async function confirmar({
        justificativa,
    }: { justificativa?: string } = {}) {
        setIsCalculando(true)
        return handleCalculo({
            navigateTo: `/relatorios/espelho-ponto?competence=${competencia}`,
            view,
            justificativa,
            isTrusted: true,
            competencia,
        }).finally(() => {
            setIsCalculando(false)
            setOpen(null)
        })
    }

    async function verificarCalcular() {
        if (view == 'mensal') {
            const res = await calculo
                .get({
                    funcionarioId,
                    competencia,
                })
                .catch(err => {
                    console.error(err)
                    return []
                })
            const aceite = (res?.at(0) as any)?.aceito
            if (aceite != null) setOpen(aceite)
            else await confirmar()
        } else {
            const res = await calculo
                .get({
                    empresaId,
                    competencia,
                })
                .catch(err => {
                    console.error(err)
                    return []
                })
            const aceite = (
                (res?.find((item: any) => item.aceito === 'S') as any) ??
                (res?.find((item: any) => item.aceito === 'N') as any) ?? {
                    aceito: null,
                }
            ).aceito
            if (aceite) setOpen(aceite)
            else await confirmar()
        }
    }

    return hasPermission(['create', 'CálculoDoPonto'], () => [
        <Button
            key="botao-calcular"
            onClick={verificarCalcular}
            disabled={isCalculando}
            startIcon={
                isCalculando ? <CircularProgress size={24} /> : undefined
            }
        >
            <span>Calcular</span>&nbsp;
            <span className="hidden lg:block">
                {dayjs(competencia, 'YYYY-MM').format('MMMM')}
            </span>
        </Button>,
        <Dialog.Root
            key="dialog-calculo"
            width="auto"
            height="auto"
            open={!!open}
            onClose={() => setOpen(null)}
            loading={isCalculando}
            onSubmit={confirmar}
            schema={z.object(
                open === 'S'
                    ? {
                          justificativa: z
                              .string()
                              .trim()
                              .min(1, 'Campo obrigatório')
                              .max(150),
                      }
                    : {},
            )}
        >
            {view == 'diario' ? (
                <Dialog.Title>
                    Um ou mais espelhos de ponto desse mês foram{' '}
                    {open == 'S' ? 'aceitos' : 'recusados'}
                </Dialog.Title>
            ) : (
                <Dialog.Title>
                    <span>
                        O espelho do ponto desse funcionário foi{' '}
                        {open == 'S' ? 'aceito' : 'recusado'}
                    </span>
                </Dialog.Title>
            )}
            {view == 'diario' ? (
                <Dialog.Content>
                    <span style={{ width: '100%', textAlign: 'center' }}>
                        Tem certeza que deseja recalcular o espelho e apagar o
                        aceite de todos os espelhos de ponto desse mês?
                    </span>
                    {open === 'S' && (
                        <FormField
                            name="justificativa"
                            label="Justificativa"
                            multiline
                            required
                            max={190}
                        />
                    )}
                </Dialog.Content>
            ) : (
                <Dialog.Content>
                    <span>
                        Tem certeza que deseja recalcular o espelho e apagar o
                        aceite?
                    </span>
                    <br />
                    {open === 'S' && (
                        <FormField
                            name="justificativa"
                            label="Justificativa"
                            multiline
                            required
                            max={190}
                        />
                    )}
                </Dialog.Content>
            )}
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton variant="outlined" />
                <Dialog.Actions.PrimaryButton>
                    Confirmar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>,
    ])
}
